<template>
	<RangeNotchedSlider
		:model-value="preferredModelValue"
		:min="preferredMin"
		:max="preferredMax"
		:thresholds="preferredThresholds"
		:label="`${label} (${pricePerAreaUnitSymbol})`"
		:unit-suffix="` ${pricePerAreaUnitSymbol}`"
		:precision="2"
		@update:model-value="updateModelValue"
		@change="change => emit('change', change)"
	/>
</template>

<script setup lang="ts">
import { computed } from "vue"
import RangeNotchedSlider from "@/components/ui/RangeNotchedSlider.vue"
import { useUnitSystem } from "@/composables/useUnitSystem"
import { AreaUnit } from "@/utils/unitSystem"
import { roundToCleanNumber } from "@/utils/format"

const props = defineProps<{
	modelValue: [number, number]
	min: number
	max: number
	thresholds: [number, number, number]
	label: string
}>()

const emit = defineEmits<{
	"update:modelValue": [value: [number, number]]
	change: [value: { from: [number, number]; to: [number, number] }]
}>()

const { areaUnit, pricePerAreaUnitSymbol, convertPricePerAreaToPreferredUnit, convertPricePerAreaFromPreferredUnit } =
	useUnitSystem()
const toPreferred = (value: number) => convertPricePerAreaToPreferredUnit(value, AreaUnit.SquareFeet)
const preferredMin = computed(() => toPreferred(props.min))
const preferredMax = computed(() => toPreferred(props.max))
const preferredModelValue = computed<[number, number]>(() => props.modelValue.map(toPreferred) as [number, number])
const preferredThresholds = computed<[number, number, number]>(() => {
	const converted = props.thresholds.map(toPreferred) as [number, number, number]
	return areaUnit.value === AreaUnit.SquareMeters
		? (converted.map(roundToCleanNumber) as [number, number, number])
		: converted
})

function updateModelValue(value: [number, number]): void {
	// Preserve untouched values, exact presets, and open ends through the unit conversion.
	const canonicalBounds = [props.min, ...props.thresholds, props.max]
	const canonical = value.map((bound, index) => {
		if (bound === preferredModelValue.value[index]) return props.modelValue[index]
		const preset = canonicalBounds.find(candidate => toPreferred(candidate) === bound)
		return preset ?? convertPricePerAreaFromPreferredUnit(bound, AreaUnit.SquareFeet)
	}) as [number, number]
	emit("update:modelValue", canonical)
}
</script>
